import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Grid3X3, List, Package, ShoppingBag, Filter, X } from "lucide-react";

const ProductList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch products
        const response = await axios.get(`${API_BASE_URL}/api/products/all`);
        const formattedProducts = response.data.map((product) => ({
          id: product.productId,
          name: product.productName,
          price: product.price,
          image: product.imageUrl,
          seller: product.sellerName || "ไม่ระบุชื่อร้านค้า",
          categoryId: product.categoryId,
        }));
        setProducts(formattedProducts);

        // Fetch categories
        const catResponse = await axios.get(`${API_BASE_URL}/api/categories`);
        setCategories(catResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Update selectedCategory when URL params change
  useEffect(() => {
    const categoryFromUrl = searchParams.get("category");
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [searchParams]);

  // Filter products by search term and category
  useEffect(() => {
    let filtered = [...products];

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(product =>
        String(product.categoryId) === String(selectedCategory)
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  }, [searchTerm, products, selectedCategory]);

  // Handle category change
  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    if (categoryId) {
      setSearchParams({ category: categoryId });
    } else {
      setSearchParams({});
    }
  };

  // Get selected category name
  const getSelectedCategoryName = () => {
    if (!selectedCategory) return null;
    const category = categories.find(c => String(c.categoryId) === String(selectedCategory));
    return category ? category.name : null;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header Section */}
      <div className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold mb-2 text-white">
                {getSelectedCategoryName() || "สินค้าทั้งหมด"}
              </h1>
              <p className="text-white/80">
                {selectedCategory
                  ? `แสดงสินค้าในหมวดหมู่ ${filteredProducts.length} รายการ`
                  : `ค้นพบสินค้าคุณภาพกว่า ${products.length} รายการ`
                }
              </p>
            </div>

            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="ค้นหาสินค้า..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Products Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {/* Header with filters */}
          <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-5 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <p className="text-gray-600">
                พบ <span className="font-semibold text-gray-900">{filteredProducts.length}</span> รายการ
              </p>

              {/* Category Filter Dropdown */}
              <div className="flex items-center gap-2">
                <Filter size={16} className="text-gray-400" />
                <select
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
                >
                  <option value="">ทุกหมวดหมู่</option>
                  {categories.map((category) => (
                    <option key={category.categoryId} value={category.categoryId}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Clear Filter Button */}
              {selectedCategory && (
                <button
                  onClick={() => handleCategoryChange("")}
                  className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
                >
                  <X size={16} />
                  ล้างตัวกรอง
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-lg transition-all ${viewMode === "grid" ? "bg-red-100 text-red-600" : "text-gray-500 hover:bg-gray-200"}`}
              >
                <Grid3X3 size={20} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-lg transition-all ${viewMode === "list" ? "bg-red-100 text-red-600" : "text-gray-500 hover:bg-gray-200"}`}
              >
                <List size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Products */}
        <div className="p-6">
          {/* Loading State */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="bg-gray-50 rounded-xl overflow-hidden animate-pulse">
                  <div className="aspect-square bg-gray-200" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">ไม่พบสินค้า</h3>
              <p className="text-gray-600">ลองปรับเปลี่ยนตัวกรองหรือคำค้นหาของคุณ</p>
            </div>
          ) : (
            <div className={viewMode === "grid"
              ? "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
              : "space-y-4"
            }>
              {filteredProducts.map((product) => (
                viewMode === "grid" ? (
                  <ProductCardGrid key={product.id} product={product} />
                ) : (
                  <ProductCardList key={product.id} product={product} />
                )
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Grid View Card
const ProductCardGrid = ({ product }) => (
  <Link
    to={`/product-details/${product.id}`}
    className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
  >
    <div className="relative aspect-square bg-gray-100 overflow-hidden">
      <img
        src={`${API_BASE_URL}${product.image}`}
        alt={product.name}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        loading="lazy"
        onError={(e) => { e.target.src = "https://via.placeholder.com/300?text=No+Image"; }}
      />
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <span className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-900 rounded-lg font-medium text-sm">
          <ShoppingBag size={16} />
          ดูสินค้า
        </span>
      </div>
    </div>
    <div className="p-4">
      <h3 className="font-medium text-gray-900 group-hover:text-red-600 transition-colors truncate">
        {product.name}
      </h3>
      <p className="mt-1 text-lg font-bold text-red-600">
        ฿{Number(product.price).toLocaleString()}
      </p>
    </div>
  </Link>
);

// List View Card
const ProductCardList = ({ product }) => (
  <Link
    to={`/product-details/${product.id}`}
    className="group flex gap-4 bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all p-4"
  >
    <div className="w-24 h-24 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
      <img
        src={`${API_BASE_URL}${product.image}`}
        alt={product.name}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        loading="lazy"
        onError={(e) => { e.target.src = "https://via.placeholder.com/300?text=No+Image"; }}
      />
    </div>
    <div className="flex-1 min-w-0">
      <h3 className="font-medium text-gray-900 group-hover:text-red-600 transition-colors truncate">
        {product.name}
      </h3>
      <p className="text-sm text-gray-600 mt-1">{product.seller}</p>
      <p className="mt-2 text-lg font-bold text-red-600">
        ฿{Number(product.price).toLocaleString()}
      </p>
    </div>
    <div className="flex items-center">
      <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 group-hover:bg-red-600 group-hover:text-white rounded-lg font-medium text-sm transition-all">
        ดูสินค้า
      </span>
    </div>
  </Link>
);

export default ProductList;
